import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Tabs, Tab,
  IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import {
  AccountBalance, Refresh, CheckCircle, HourglassTop, ExpandMore, Receipt as ReceiptIcon,
} from '@mui/icons-material';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { colors } from '../theme/theme';
import { isStudioScopedRole } from '../utils/roles';

const inr = (n: number) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtPeriod = (s?: string, e?: string) => {
  if (!s || !e) return '—';
  const a = new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  const b = new Date(e).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return `${a} – ${b}`;
};
const monthKey = (d: string) => { const x = new Date(d); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`; };
const monthLabel = (k: string) => { const [y, m] = k.split('-'); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }); };

const StatusChip: React.FC<{ s: any }> = ({ s }) => (
  s.status === 'paid'
    ? <Chip size="small" label="Paid" sx={{ bgcolor: '#2e7d3215', color: '#2e7d32', fontWeight: 700 }} />
    : <Chip size="small" label={s.isOverdue ? 'Overdue' : 'Pending'} sx={{ bgcolor: s.isOverdue ? '#d32f2f15' : '#ed6c0215', color: s.isOverdue ? '#d32f2f' : '#ed6c02', fontWeight: 700 }} />
);

const StudioPayoutsPage: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const isStudioRole = isStudioScopedRole(admin?.role);
  const isSuperAdmin = !isStudioRole;

  useEffect(() => {
    if (admin?.role === 'super_admin') {
      navigate('/studio-payments', { replace: true });
    }
  }, [admin?.role, navigate]);

  const [statements, setStatements] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusTab, setStatusTab] = useState<'all' | 'pending' | 'paid'>('all');
  const [expanded, setExpanded] = useState<string | false>(false);

  const [receiptLoadingId, setReceiptLoadingId] = useState<string | null>(null);

  // Open the detailed invoice PDF (member line items + GST + settlement summary)
  // — the same document emailed on mark-paid. Studio owner uses the scoped
  // endpoint; super-admin uses the studio-invoice endpoint for that week.
  const openReceipt = async (s: any) => {
    setReceiptLoadingId(s._id);
    try {
      // The studio-scoped receipt endpoint reads the discount + adjustment off
      // the statement itself; the super-admin invoice endpoint recomputes from
      // raw revenue, so they have to be passed explicitly or the PDF shows the
      // pre-adjustment net while the table shows the real payable amount.
      const blob: Blob = isStudioRole
        ? await apiService.getStudioPayoutReceipt(s._id)
        : await apiService.generateStudioInvoice(s.studio?._id, {
            startDate: s.weekStart,
            endDate: s.weekEnd,
            discountAmount: (s.discountAmount ?? 0) > 0 ? s.discountAmount : undefined,
            discountLabel: (s.discountAmount ?? 0) > 0 ? s.discountLabel : undefined,
            adjustmentAmount: (s.adjustmentAmount ?? 0) !== 0 ? s.adjustmentAmount : undefined,
            adjustmentLabel: (s.adjustmentAmount ?? 0) !== 0 ? s.adjustmentLabel : undefined,
          });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setError('Could not open the receipt.');
    } finally {
      setReceiptLoadingId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isStudioRole) {
        const [res, sum]: any = await Promise.all([
          apiService.getStudioPayouts({ pageSize: 100, page: 1, status: statusTab }),
          apiService.getStudioPayoutSummary(),
        ]);
        setStatements(res.statements || []);
        setSummary(sum || null);
      } else {
        const res: any = await apiService.getStudioPaymentStatements({ pageSize: 200, page: 1, status: statusTab });
        setStatements(res.statements || []);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [isStudioRole, statusTab]);

  useEffect(() => { load(); }, [load]);

  // Group by month (recent first); weeks within a month recent first.
  const months = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const s of statements) {
      if (!s.weekStart) continue;
      const k = monthKey(s.weekStart);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(s);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([key, list]) => ({
        key,
        label: monthLabel(key),
        items: list.sort((a, b) => new Date(b.weekStart).getTime() - new Date(a.weekStart).getTime()),
        // Use the server's finalSettlement (net + adjustment − discount) so the
        // month totals match what is actually transferred. Older responses
        // without the field fall back to the raw net.
        paidNet: list.filter(x => x.status === 'paid').reduce((t, x) => t + (x.finalSettlement ?? x.netSettlement ?? 0), 0),
        pendingNet: list.filter(x => x.status !== 'paid').reduce((t, x) => t + (x.finalSettlement ?? x.netSettlement ?? 0), 0),
      }));
  }, [statements]);

  // Default-expand the most recent month.
  useEffect(() => { if (months.length && expanded === false) setExpanded(months[0].key); }, [months, expanded]);


  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalance sx={{ color: colors.primary }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Payouts</Typography>
            <Typography variant="caption" color="text.secondary">
              {isSuperAdmin ? 'Weekly settlement history — all studios' : 'Your weekly settlements'} · Friday → Thursday
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Summary (studio owners) */}
      {isStudioRole && summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
          <SummaryCard label="Lifetime earnings" value={inr(summary.totalPaid)} icon={<CheckCircle />} tint="#2e7d32" sub={`${summary.paidCount} paid`} />
          <SummaryCard label="Pending payout" value={inr(summary.totalPending)} icon={<HourglassTop />} tint="#ed6c02" sub={`${summary.pendingCount} pending`} />
          <SummaryCard label="Total statements" value={String(summary.statementCount)} icon={<AccountBalance />} tint={colors.primary} />
          <SummaryCard label="Current week" value={fmtDate(summary.currentWeekStart)} icon={<HourglassTop />} tint="#0288d1" sub="Fri → Thu" />
        </Box>
      )}

      <Card sx={{ border: `1px solid ${colors.divider}` }}>
        <Tabs value={statusTab} onChange={(_, v) => setStatusTab(v)} sx={{ px: 2, borderBottom: `1px solid ${colors.divider}` }}>
          <Tab value="all" label="All" sx={{ textTransform: 'none' }} />
          <Tab value="paid" label="Paid" sx={{ textTransform: 'none' }} />
          <Tab value="pending" label="Pending" sx={{ textTransform: 'none' }} />
        </Tabs>

        {loading ? (
          <Box p={4} textAlign="center"><CircularProgress /></Box>
        ) : months.length === 0 ? (
          <Box p={5} textAlign="center">
            <AccountBalance sx={{ fontSize: 40, color: `${colors.primary}55`, mb: 1 }} />
            <Typography color="text.secondary">No payouts in this view yet.</Typography>
          </Box>
        ) : (
          <Box p={2}>
            {months.map(m => (
              <Accordion
                key={m.key}
                expanded={expanded === m.key}
                onChange={() => setExpanded(expanded === m.key ? false : m.key)}
                disableGutters
                sx={{ '&:before': { display: 'none' }, border: `1px solid ${colors.divider}`, borderRadius: 2, mb: 1.5, overflow: 'hidden' }}
              >
                <AccordionSummary expandIcon={<ExpandMore />} sx={{ bgcolor: `${colors.primary}06` }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={2}>
                    <Typography fontWeight={700}>{m.label}</Typography>
                    <Box display="flex" gap={2} alignItems="center">
                      <Typography variant="caption" color="text.secondary">{m.items.length} week{m.items.length !== 1 ? 's' : ''}</Typography>
                      <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 700 }}>{inr(m.paidNet)} paid</Typography>
                      {m.pendingNet > 0 && <Typography variant="body2" sx={{ color: '#ed6c02', fontWeight: 700 }}>{inr(m.pendingNet)} pending</Typography>}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Week</TableCell>
                        {isSuperAdmin && <TableCell sx={{ fontWeight: 700 }}>Studio</TableCell>}
                        <TableCell sx={{ fontWeight: 700 }} align="right">Total</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Net payout</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Paid on</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Receipt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {m.items.map(s => (
                        <TableRow key={s._id} hover>
                          <TableCell>{fmtPeriod(s.weekStart, s.weekEnd)}</TableCell>
                          {isSuperAdmin && <TableCell>{s.studio?.studioName || '—'}</TableCell>}
                          <TableCell align="right">{inr(s.totalRevenue)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {inr(s.finalSettlement ?? s.netSettlement)}
                            {(s.adjustmentAmount ?? 0) !== 0 && (
                              <Typography variant="caption" sx={{ display: 'block', color: (s.adjustmentAmount ?? 0) > 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                                incl. {(s.adjustmentAmount ?? 0) > 0 ? '+' : '−'}{inr(Math.abs(s.adjustmentAmount))} {s.adjustmentLabel || 'adjustment'}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell><StatusChip s={s} /></TableCell>
                          <TableCell>{s.status === 'paid' ? fmtDate(s.paidAt) : '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip title="View / download receipt">
                              <span>
                                <IconButton size="small" onClick={() => openReceipt(s)} disabled={receiptLoadingId === s._id}>
                                  {receiptLoadingId === s._id ? <CircularProgress size={16} /> : <ReceiptIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}
      </Card>
    </Box>
  );
};

const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; tint: string; sub?: string }> = ({ label, value, icon, tint, sub }) => (
  <Card sx={{ border: `1px solid ${colors.divider}` }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: `${tint}1a`, color: tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</Box>
      <Box>
        <Typography variant="h6" fontWeight={800}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {sub && <Typography variant="caption" color="text.secondary" display="block">{sub}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

export default StudioPayoutsPage;
